import { useCallback } from 'react'
import { useStore } from 'react-redux'
import { useRouter } from 'next/router'
import type { Persistor } from 'redux-persist'
import { captureException } from '@sentry/nextjs'

import ROUTES from '~/shared/constants/routes.const'
import appConfig from '~/config/app'
import { clearBrowserSession } from '~/auth/utils/clearBrowserSession'
import { resetSchoolDevice } from '~/classroom/utils/resetSchoolDevice'
import { useAppDispatch } from '~/redux/hooks'
import { useSchoolSsoLogoutMutation } from '~/classroom/apis/classroom.api'

/**
 * OK-10 — sign out of House of Math, and of the district's identity provider where there is one.
 *
 * ⚠ **Signing out of us does not sign a pupil out of her district.** That session is a cookie on the
 * provider's domain. Without this, the next child at the same laptop clicks "sign in" and is silently
 * admitted as her — no prompt, no password.
 */

/**
 * ⚠ The one place this deliberately differs from the plain sign-out it replaces, which cleared the
 * session synchronously so "a slow or failing logout call cannot strand the user on a signed-in
 * screen". We have to ask the server *before* clearing, because the route reads the caller off the
 * verified token — so the wait is bounded instead. Past this, the user is signed out locally and the
 * provider's session simply outlives ours, which `prompt=login` already covers.
 */
const LOGOUT_URL_TIMEOUT_MS = 2500

/**
 * ⚠ Used by all THREE sign-out buttons in the school edition, and it has to be: the pupil's is
 * `menu/ButtonLogout` on `/dashboard`, not either teacher menu. Wiring only the teacher path would
 * leave the shared-Chromebook defence covering the population it is least about.
 */
export default function useSchoolSignOut(onBeforeSignOut?: () => void): () => void {
  const router = useRouter()
  const dispatch = useAppDispatch()
  // `__persistor` is the hack `makeStore` hangs on the store; see `store.ts`.
  const store = useStore() as { __persistor?: Persistor }
  const [ssoLogout] = useSchoolSsoLogoutMutation()

  const signOut = useCallback(async (): Promise<void> => {
    let redirectUrl: string | null = null

    if (appConfig.isSchoolPlatform) {
      try {
        const result = await Promise.race([
          ssoLogout().unwrap(),
          new Promise<{ redirectUrl: null }>((resolve) =>
            setTimeout(() => resolve({ redirectUrl: null }), LOGOUT_URL_TIMEOUT_MS),
          ),
        ])

        redirectUrl = result.redirectUrl
      } catch {
        // A password login, an aged-out hint, an unreachable server. All of them mean the same thing
        // here: sign out locally and carry on. Never block the sign-out on this.
      }
    }

    onBeforeSignOut?.()

    try {
      /**
       * Always, and after the ask rather than before it — the ask needs the token this drops.
       *
       * ⚠ Bounded like the ask. The tokens and caches go synchronously inside it, but it then awaits
       * `/v1/auth/logout` and Firebase, and `axiosInstance` has no timeout — unbounded, a hung call
       * keeps the pupil's name on screen and the navigation below never runs.
       */
      await Promise.race([
        dispatch(clearBrowserSession()),
        new Promise((resolve) => setTimeout(resolve, LOGOUT_URL_TIMEOUT_MS)),
      ])

      /**
       * The whole device, not just the token: `clearBrowserSession` purges and then dispatches, and every
       * dispatch writes a fresh snapshot of whatever survived its reset. Done here rather than in
       * `clearBrowserSession`: that function is on the b2c sign-out path too.
       */
      await resetSchoolDevice(dispatch, store.__persistor)
    } catch (error) {
      // A throwing flush (storage quota on a well-used shared iPad) must not skip the navigation below.
      captureException(error, { tags: { feature: 'auth', action: 'school-sign-out' } })
    }

    if (redirectUrl) {
      // `assign`, not the Next router: this leaves the app for the provider's domain, and Next's
      // router would try to resolve an external URL as a route.
      window.location.assign(redirectUrl)

      return
    }

    /**
     * ⚠ `replace`, not `push`. Back must not return to a signed-in screen — S-11 asks for that
     * explicitly, and on a shared cart of laptops it is the difference between a sign-out and a
     * suggestion.
     */
    await router.replace(ROUTES.HOME)
  }, [dispatch, onBeforeSignOut, router, ssoLogout, store])

  return () => void signOut()
}
